/**
 * Shopify CRM Config Modal
 * Configure store names, import data, and manage Shopify data
 */

import { useState, useEffect, useRef } from 'react';
import type { PluginConfigModalProps } from '../_infrastructure/pluginTypes';
import { env } from '@/lib/env';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { getAuthHeadersNoWorkspace as getAuthHeaders } from './lib/auth';

export function ConfigModal(props: PluginConfigModalProps) {
  const {
    isOpen,
    onClose,
    config,
    onSave,
    instanceId,
  } = props;
  const [modalContentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick(onClose, 30);
  const [stores, setStores] = useState(config.stores || '');
  const [isSaving, setIsSaving] = useState(false);

  // Clear data state
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [clearResult, setClearResult] = useState<{ success: boolean; count: number } | null>(null);

  // Import state
  const [importStep, setImportStep] = useState<'idle' | 'parsed' | 'importing' | 'done' | 'error'>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [storeName, setStoreName] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{ created: number; updated: number; skipped?: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setStores(config.stores || '');
      // Reset action states when modal opens
      setShowClearConfirm(false);
      setClearResult(null);
      resetImport();
    }
  }, [isOpen, config]);

  const resetImport = () => {
    setImportStep('idle');
    setSelectedFile(null);
    setStoreName('');
    setImportError(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen) return null;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({ stores: stores.trim() });
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearData = async () => {
    setIsClearing(true);
    try {
      // Use dedicated plugin endpoint to clear Shopify data
      const response = await fetch(
        `${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/shopify-crm/data`,
        {
        method: 'DELETE',
        headers: getAuthHeaders(),
        }
      );

      if (!response.ok) throw new Error('Failed to clear data');

      const data = await response.json();
      setClearResult({ success: true, count: data.cleared || 0 });
      setShowClearConfirm(false);
    } catch (err) {
      console.error('Failed to clear Shopify data:', err);
      setClearResult({ success: false, count: 0 });
    } finally {
      setIsClearing(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file is CSV
    if (!file.name.endsWith('.csv')) {
      setImportError('Please select a CSV file');
      setImportStep('error');
      return;
    }

    setSelectedFile(file);
    setImportStep('parsed');
    setImportError(null);
  };

  const handleImport = async () => {
    if (!selectedFile || !storeName.trim()) return;

    setImportStep('importing');
    setImportError(null);

    try {
      // Use dedicated plugin import endpoint (single request)
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('storeName', storeName.trim());

      const response = await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/shopify-crm/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Import failed' }));
        throw new Error(err.error || err.details || 'Import failed');
      }

      const data = await response.json();
      setImportResult({
        created: data.summary?.created || 0,
        updated: data.summary?.updated || 0,
      });
      setImportStep('done');
    } catch (err) {
      setImportError((err as Error).message);
      setImportStep('error');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={handleOverlayMouseDown}

      onClick={handleOverlayClick}
    >
      <div
        ref={modalContentRef}
        className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto xv-scroll"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white rounded-t-xl">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🛒</span>
            <h2 className="text-lg font-semibold">Shopify CRM Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Quick Actions Section */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h3>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
                disabled={importStep === 'importing'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Import Orders
              </button>
              <button
                onClick={() => setShowClearConfirm(true)}
                className="flex-1 px-3 py-2 text-sm text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 flex items-center justify-center gap-2"
                disabled={isClearing}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Clear Data
              </button>
            </div>
          </div>

          {/* Import Flow - shown when importing */}
          {importStep !== 'idle' && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              {importStep === 'parsed' && selectedFile && (
                <div className="space-y-3">
                  <p className="text-sm text-purple-700">
                    Ready to import <strong>{selectedFile.name}</strong>
                  </p>
                  <div>
                    <label className="block text-xs font-medium text-purple-700 mb-1">Store Name</label>
                    <input
                      type="text"
                      value={storeName}
                      onChange={(e) => setStoreName(e.target.value)}
                      placeholder="e.g., My Shopify Store"
                      className="w-full px-3 py-2 text-sm border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={resetImport}
                      className="flex-1 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleImport}
                      disabled={!storeName.trim()}
                      className="flex-1 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                      Import
                    </button>
                  </div>
                </div>
              )}

              {importStep === 'importing' && (
                <p className="text-sm text-purple-700">Importing customers...</p>
              )}

              {importStep === 'done' && importResult && (
                <div className="space-y-2">
                  <p className="text-sm text-green-700 font-medium">
                    Import complete! Created {importResult.created}, updated {importResult.updated} contacts.
                  </p>
                  <button
                    onClick={resetImport}
                    className="w-full px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Done
                  </button>
                </div>
              )}

              {importStep === 'error' && (
                <div className="space-y-2">
                  <p className="text-sm text-red-600">{importError}</p>
                  <button
                    onClick={resetImport}
                    className="w-full px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Clear Confirmation */}
          {showClearConfirm && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700 mb-3">
                This will remove all Shopify purchase history from contacts. The contacts themselves will remain.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="flex-1 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={isClearing}
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearData}
                  className="flex-1 px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  disabled={isClearing}
                >
                  {isClearing ? 'Clearing...' : 'Clear Data'}
                </button>
              </div>
            </div>
          )}

          {/* Clear Result */}
          {clearResult && (
            <div className={`rounded-lg p-3 ${clearResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <p className={`text-sm ${clearResult.success ? 'text-green-700' : 'text-red-700'}`}>
                {clearResult.success
                  ? `Cleared Shopify data from ${clearResult.count} contacts.`
                  : 'Failed to clear data. Please try again.'}
              </p>
            </div>
          )}

          {/* Store Names Setting */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Store Names (Optional)
            </label>
            <input
              type="text"
              value={stores}
              onChange={(e) => setStores(e.target.value)}
              placeholder="e.g., Quantum Goods, AQ Merch"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Comma-separated list of your Shopify store names (for reference)
            </p>
          </div>

          {/* Instructions */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <h3 className="text-sm font-medium text-purple-800 mb-2">
              How to Export from Shopify
            </h3>
            <ol className="text-xs text-purple-700 space-y-1.5 list-decimal list-inside">
              <li>Go to your Shopify Admin → Orders</li>
              <li>Click <strong>Export</strong> → Select "All orders" or a date range</li>
              <li>Download the CSV file</li>
              <li>Use the "Import Orders" button above</li>
            </ol>
            <p className="text-xs text-purple-600 mt-2">
              Customers will be matched by email to existing contacts.
            </p>
          </div>

          {/* Chat Commands */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <h3 className="text-sm font-medium text-gray-700 mb-1">
              Chat Commands
            </h3>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• "Has John Smith bought from us?"</li>
              <li>• "Who are our top customers?"</li>
              <li>• "Show customers who spent over $1000"</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t sticky bottom-0 bg-white rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Close
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
